import { useCallback, useEffect, useState } from 'react';

import {
  getBitrixSettings,
  listBitrixLogs,
  listBitrixQueueRows,
  type BitrixLogRow,
  type BitrixQueueRow,
  type BitrixSettings,
} from '@/lib/db/admin/bitrix24';

type AsyncStatus = 'loading' | 'success' | 'error';

export type BitrixSettingsState = {
  status: AsyncStatus;
  settings: BitrixSettings | null;
  error: Error | null;
  refetch: () => void;
};

export function useBitrixSettings(): BitrixSettingsState {
  const [state, setState] = useState<Omit<BitrixSettingsState, 'refetch'>>({
    status: 'loading',
    settings: null,
    error: null,
  });
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, status: 'loading', error: null }));
    getBitrixSettings()
      .then((settings) => {
        if (cancelled) return;
        setState({ status: 'success', settings, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          settings: null,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [tick]);

  return { ...state, refetch };
}

export type BitrixQueueState = {
  status: AsyncStatus;
  rows: BitrixQueueRow[];
  error: Error | null;
  refetch: () => void;
};

export function useBitrixQueueRows(rowStatus: 'failed_config' | 'abandoned'): BitrixQueueState {
  const [state, setState] = useState<Omit<BitrixQueueState, 'refetch'>>({
    status: 'loading',
    rows: [],
    error: null,
  });
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, status: 'loading', error: null }));
    listBitrixQueueRows(rowStatus)
      .then((rows) => {
        if (cancelled) return;
        setState({ status: 'success', rows, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          rows: [],
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [rowStatus, tick]);

  return { ...state, refetch };
}

export type BitrixLogsState = {
  status: AsyncStatus;
  rows: BitrixLogRow[];
  error: Error | null;
  refetch: () => void;
};

export function useBitrixLogs(limit = 50): BitrixLogsState {
  const [state, setState] = useState<Omit<BitrixLogsState, 'refetch'>>({
    status: 'loading',
    rows: [],
    error: null,
  });
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, status: 'loading', error: null }));
    listBitrixLogs(limit)
      .then((rows) => {
        if (cancelled) return;
        setState({ status: 'success', rows, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          rows: [],
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [limit, tick]);

  return { ...state, refetch };
}
